import type { SessionType } from '@/util/types/globalTypes';
import { frontendLogger } from './loggerCore';
import {
  formatLogMessageWithIdentity,
  type ObservabilityContext,
} from './observability';

const WEB = { surface: 'web' as const };

/**
 * Use when logging auth events after the session is cleared (e.g. signOut onSuccess) or
 * when telemetry tags are still empty, supplying company_id / user_id explicitly.
 */
export function authSessionLogContext(
  session: SessionType | null | undefined
): ObservabilityContext {
  if (!session?.user) return {};
  const u = session.user;
  const user_id = String(u._id);
  const companyRaw = u.company?._id ?? u.membership?._companyId ?? u._companyId;
  const company_id =
    companyRaw != null && String(companyRaw).trim() !== ''
      ? String(companyRaw)
      : 'unknown';
  return { company_id, user_id, attik_user_id: user_id };
}

export function authLoginAttempt(context?: ObservabilityContext): void {
  frontendLogger.info(formatLogMessageWithIdentity('Auth: login attempt', context), {
    category: 'auth',
    action: 'login',
    result: 'started',
    ...WEB,
    ...context,
  });
}

export function authLoginSuccess(context?: ObservabilityContext): void {
  frontendLogger.info(formatLogMessageWithIdentity('Auth: login success', context), {
    category: 'auth',
    action: 'login',
    result: 'success',
    ...WEB,
    ...context,
  });
}

export function authLoginFailed(
  error: Error | string,
  context?: ObservabilityContext
): void {
  frontendLogger.warn(formatLogMessageWithIdentity('Auth: login failed', context), {
    category: 'auth',
    action: 'login',
    result: 'error',
    ...WEB,
    error: error instanceof Error ? error.message : error,
    ...context,
  });
}

export function authSessionExpired(context?: ObservabilityContext): void {
  frontendLogger.warn(formatLogMessageWithIdentity('Auth: session expired', context), {
    category: 'auth',
    action: 'session_expired',
    result: 'warning',
    ...WEB,
    ...context,
  });
}

/** Magic link send requested (no email in context — avoid PII). */
export function authMagicLinkRequestStarted(context?: ObservabilityContext): void {
  frontendLogger.info(
    formatLogMessageWithIdentity('Auth: magic link request started', context),
    {
      category: 'auth',
      action: 'magic_link',
      result: 'started',
      ...WEB,
      ...context,
    }
  );
}

export function authMagicLinkSent(context?: ObservabilityContext): void {
  frontendLogger.info(
    formatLogMessageWithIdentity('Auth: magic link email sent', context),
    {
      category: 'auth',
      action: 'magic_link',
      result: 'success',
      ...WEB,
      ...context,
    }
  );
}

export function authMagicLinkSendFailed(
  error: Error | string,
  context?: ObservabilityContext
): void {
  frontendLogger.warn(
    formatLogMessageWithIdentity('Auth: magic link send failed', context),
    {
      category: 'auth',
      action: 'magic_link',
      result: 'error',
      ...WEB,
      error: error instanceof Error ? error.message : error,
      ...context,
    }
  );
}

export function authSocialSignInStarted(
  provider: string,
  context?: ObservabilityContext
): void {
  frontendLogger.info(
    formatLogMessageWithIdentity('Auth: social sign-in started', context),
    {
      category: 'auth',
      action: 'social_sign_in',
      result: 'started',
      provider,
      ...WEB,
      ...context,
    }
  );
}

export function authSignOut(context?: ObservabilityContext): void {
  frontendLogger.info(formatLogMessageWithIdentity('Auth: sign out', context), {
    category: 'auth',
    action: 'sign_out',
    result: 'success',
    ...WEB,
    ...context,
  });
}

export const authLogger = {
  loginAttempt: authLoginAttempt,
  loginSuccess: authLoginSuccess,
  loginFailed: authLoginFailed,
  sessionExpired: authSessionExpired,
  magicLinkRequestStarted: authMagicLinkRequestStarted,
  magicLinkSent: authMagicLinkSent,
  magicLinkSendFailed: authMagicLinkSendFailed,
  socialSignInStarted: authSocialSignInStarted,
  signOut: authSignOut,
  sessionLogContext: authSessionLogContext,
};
